import { useState } from 'react';

import {
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Input,
} from '@unified-checkout/design-system';
import type { IColumn } from 'libs/design-system/src/types/modal/modal_body';

import type { ICustomReportModal } from '@app/dashboard/types/innerLayout/reports/modals/customReports';

import DateRanges from '../../../DateRanges';

import styles from './CustomReportsModalBody.module.scss';

export const CustomReportsModalBody = (props: ICustomReportModal) => {  
  const {
    localization,
    onNameChange,
    onColumnsChange,
    onSaveAsGeneralChange,
    availableColumns = [],
    appliedRanges,
    handleRangesChange,
    lang,
    containerRef,
    buttonLabel,
    datePickerLabel,
  } = props;  

  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>([]);
  const [localSelectedColumns, setLocalSelectedColumns] = useState<IColumn[]>(
    []
  );
  const [saveAsGeneral, setSaveAsGeneral] = useState(false);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange?.(e.target.value);
  };

  const handleApply = () => {
    setSelectedColumns(localSelectedColumns);
    onColumnsChange?.(localSelectedColumns);
  };

  const handleToggle = (isOpen: boolean) => {
    if (!isOpen) {
      setLocalSelectedColumns(selectedColumns);
    }
  };

  const handleSaveAsGeneralChange = (checked: boolean) => {
    setSaveAsGeneral(checked);
    onSaveAsGeneralChange?.(checked);
  };

  return (
    <div className={styles['custom-report']}>
      <div className={styles['custom-report__field']}>
        <Input
          name="report-name"
          label={localization?.name || 'Name'}
          placeholder={
            localization?.nameInputPlaceholder || 'ex. Cash flow report'
          }
          onChange={handleNameChange}
        />
      </div>

      <div
        className={`${styles['custom-report__field']} ${styles['custom-report__field__calendar']}`}
      >
        <DateRanges
          appliedRanges={appliedRanges}
          datePickerLabel={datePickerLabel}
          handleRangesChange={(ranges) => handleRangesChange(ranges)}
          lang={lang}
          containerRef={containerRef}
        />
      </div>

      <div className={styles['custom-report__field']}>
        <Dropdown label={localization?.columns || 'Columns'}>
          <DropdownButton text={buttonLabel} />
          <DropdownMenu
            type="checkboxes"
            onApplyHandler={handleApply}
            applyBtnDisabled={localSelectedColumns.length < 2}
            onToggleHandler={handleToggle}
            lang={lang}
          >
            <DropdownMenuItem
              key="select-all-columns"
              type="checkbox"
              checkboxIcon="dash"
              id="select-all-columns"
              text={`${localSelectedColumns.length} ${
                localization?.selected || 'Selected'
              }`}
              checked={localSelectedColumns.length === availableColumns.length}
              onCheckboxItemClickHandler={(checked) => {
                setLocalSelectedColumns(
                  checked
                    ? availableColumns.map((col) => ({
                        value: col.value,
                        label: col.label,
                      }))
                    : []
                );
              }}
              closeOnItemClick={false}
            />
            {availableColumns.map((column) => (
              <DropdownMenuItem
                key={`column-${column.value}`}
                type="checkbox"
                id={`column-${column.value}`}
                text={column.label}
                checked={localSelectedColumns.some(
                  (col) => col.value === column.value
                )}
                onCheckboxItemClickHandler={(checked) => {
                  setLocalSelectedColumns((prev) =>
                    checked
                      ? [...prev, column]
                      : prev.filter((value) => value.value !== column.value)
                  );
                }}
                closeOnItemClick={false}
              />
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className={styles['custom-report__field']}>
        <Checkbox
          id="save-as-general"
          text={
            localization?.saveAsGeneral ||
            'Save this report as a general report'
          }
          checked={saveAsGeneral}
          onChangeHandler={handleSaveAsGeneralChange}
        />
      </div>
    </div>
  );
};
